import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { zip } from 'underscore';
import {
  formatDate,
  formatPercent,
  formatNumber,
} from '../functions';
import i18n from '../../../i18n';
import AnalysisCard from 'wt-react/dist/components/AnalysisCard/AnalysisCard';
// import { test } from 'wwt-react/dist/util/functions';
import './Strategy.css';

class Strategy extends PureComponent {

  COLORS = {
    blue: '#0047BB',
    teal: '#2DCCD3',
    grey: '#5B6770',
    orange: '#F68D2E',
    auburn: '#84344E',
    sky: '#00A9E0',
    green: '#658D1B',
    white: '#FFFFFF',
    silver: '#BDC2C6',
  }

  KEY_METRICS = [
    'performance',
    'income',
    'risk',
    'sharpe',
    'fees',
  ]

  // Get a dictionary of the key metrics formatted correctly
  getKeyMetrics(statistics) {
    return ({
      performance: formatPercent(statistics.avgAnnualPerformance, 1, false),
      income: formatPercent(statistics.dividendYield, 1, false),
      risk: formatPercent(statistics.avgAnnualVolatility, 1, false),
      sharpe: formatNumber(statistics.sharpeRatio, 2),
      fees: formatNumber(statistics.aggregateExpenseRatio * 10000, 0),
    })
  }

  // Get a dictionary of the key metrics' symbols 
  getKeyMetricsSymbols() {
    return ({
      performance: '%',
      income: '%',
      risk: '%',
      sharpe: '',
      fees: 'bps',
    })
  }

  // Get a dictionary of which metrics are clickable (have their own charts)
  getKeyMetricsClicks() {
    return ({
      performance: false,
      income: false,
      risk: false,
      sharpe: false,
      fees: false,
    })
  }

  renderKeyMetrics() {
    const {
      strategy,
    } = this.props;

    if (strategy.latestAnalysis && strategy.latestAnalysis.statistics) {
      const metricsValues = this.getKeyMetrics(strategy.latestAnalysis.statistics)
      const metricsSymbols = this.getKeyMetricsSymbols();
      const metricClicks = this.getKeyMetricsClicks();
      // Build metric data and labels
      const metricData = this.KEY_METRICS.map(metric => {
        return {
          name: metric + 'strat',
          title: i18n.t(metric),
          tooltip: i18n.t(metric + '-tooltip')
            .replace("[[FROM]]", formatDate(strategy.latestAnalysis.commonPeriodStart))
            .replace("[[TO]]", formatDate(strategy.latestAnalysis.commonPeriodEnd))
            .replace("[[ASOF]]", formatDate(strategy.latestAnalysis.commonPeriodEnd)),
          description: i18n.t(metric + '-desc'),
          value: metricsValues[metric],
          symbol: metricsSymbols[metric],
          clickable: metricClicks[metric],
        }
      })
      return (
        <React.Fragment>
          <AnalysisCard
            idOfCard='strategy-key-metrics'
            topOfAppId=''
            key={'keymetrics-card' + strategy.id}
            chartType='keymetrics'
            cardTitle={ i18n.t('key-metrics-title') }
            seriesData={metricData}
            //footnoteText={'* As of ' + formatDate(strategy.latestAnalysis.commonPeriodEnd) + ' with a Common Period from ' + formatDate(strategy.latestAnalysis.commonPeriodStart) + ' to ' + formatDate(strategy.latestAnalysis.commonPeriodEnd) + '.'}
            //showDetailsOnHover={true}
            //metricComparisonNames={[strategy.name, '']}
          />
        </React.Fragment>
      )
    } else {
      return null;
    }
  }

  renderHoldingsChart() {
    const {
      strategy,
    } = this.props;

    if (!strategy || !strategy.holdings || strategy.holdings.length === 0) {
      return null;
    }

    // Get the latest holdings
    const holdings = strategy.holdings[strategy.holdings.length - 1];
    if (!holdings || !holdings.assetClasses || !holdings.tickers || !holdings.weights) {
      return null;
    }

    // Function to filter unique entries in array
    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }

    // Get distinct Asset Classes
    const assetClasses = holdings.assetClasses.filter(onlyUnique);
    // Zip together the relevant data
    const zippedData = zip(holdings.assetClasses, holdings.tickers, holdings.weights);

    let data = [];
    // Loop over each asset class and put the data series together
    assetClasses.forEach(function (assetClass) {
      // Find the elements in the given asset class
      let assetClassZip = zippedData.filter(zd => zd[0] === assetClass);
      // Total up the weights
      let sumWeights = assetClassZip.map((x)=>x[2]).reduce(function sumofArray(sum, num) {
        return sum + num;
      });
      // Build each treenode by asset class with holdings at the child level
      let assetClassData = {
        name: assetClass,
        value: formatPercent(sumWeights, 1, false),
        id: assetClass,
        children: assetClassZip.map(holding => {
          return {
            name: holding[1],
            value: formatPercent(holding[2], 1, false),
            id: assetClass,
            label: {
              show: true,
              formatter: '{b}',
            },
            link: '#fundchar&tickers=' + holding[1],
            target: '_self',
          }
        })
      }
      // Add that asset class to the array
      data.push(assetClassData);
    });

    return (
      <div>
        <AnalysisCard
          idOfCard='strategy-holdings'
          topOfAppId=''
          key='holdings'
          chartType='treemap'
          cardTitle={i18n.t('holdings')}
          showChartLabels={false}
          showLegend={false}
          categoryIsDate={true}
          seriesData={data}
          showGrid={false}
          showAxes={false}
          heroNumber={holdings.tickers.length}
          heroNumberIsPercent={false}
          heroTitle={i18n.t('number-of-holdings')}
          showDetailsOnHover={false}
        />
        <ReactTooltip id={'click-to-edit-holdings-tooltip' + strategy.id} type='light' className='tooltip' effect="float">
          {i18n.t('click-to-edit-holdings-tooltip')}
        </ReactTooltip>
      </div>
    )

  }

  renderPerformanceChart() {

    const personalStrategy = this.props.strategy;
    const latestAnalysis = personalStrategy && personalStrategy.latestAnalysis;
    if (!latestAnalysis || !latestAnalysis.levels || !latestAnalysis.levels.uv1MNAV || !latestAnalysis.levels.date) {
      return null;
    }

    // Add Performance Strategy data
    let seriesData = [{
        data: latestAnalysis.levels.uv1MNAV,
        name: personalStrategy.name,
        color: this.COLORS.blue,
    }];

    // If a benchmark exists then add to the portfolio performance
    if (latestAnalysis.benchmarks && latestAnalysis.benchmarks.length > 0){
        seriesData.push({
            data: latestAnalysis.benchmarks[0].levels.uv1MNAV,
            name: latestAnalysis.benchmarks[0].name,
            color: this.COLORS.silver,
        })
    }

    return (
        <React.Fragment>
                <AnalysisCard
                    minChartHeight='398px'
                    idOfCard='strategy-growth-1m'
                    topOfAppId=''
                    key='performance'
                    chartType='line'
                    cardTitle={i18n.t('growth')}
                    showChartLabels={true}
                    seriesCurrencyLabel='$'
                    showLegend={false}
                    categoryData={latestAnalysis.levels.date}
                    categoryIsDate={true}
                    seriesData={seriesData}
                    showGrid={true}
                    showAxes={true}
                    heroTitle={i18n.t('hypothetical-growth-title')}
                        //.replace("[[FROM]]", 'formatDate(personalStrategy.latestAnalysis.commonPeriodStart)')
                        //.replace("[[TO]]", formatDate(personalStrategy.latestAnalysis.commonPeriodEnd))}
                    
                    // Keep the other hero titles in case we need for dashboard
                    // chartTitle={i18n.t('hypothetical-growth-title')
                    //     .replace("[[FROM]]", formatDate(personalStrategy.latestAnalysis.commonPeriodStart))
                    //     .replace("[[TO]]", formatDate(personalStrategy.latestAnalysis.commonPeriodEnd))}
                    // heroNumber={personalStrategy.latestAnalysis.statistics.avgAnnualPerformance}
                    // heroTitle={i18n.t('common-period-total-return')
                    //     .replace("[[FROM]]", formatDate(personalStrategy.latestAnalysis.commonPeriodStart))
                    //     .replace("[[TO]]", formatDate(personalStrategy.latestAnalysis.commonPeriodEnd))}
                    //footnoteText={i18n.t('hypothetical-growth-footnote')}
                />
            </React.Fragment>
    )

  }

  render() {
    const {
      strategy,
    } = this.props;
    const latestAnalysis = strategy && strategy.latestAnalysis;
    const hasCommonPeriod = latestAnalysis && latestAnalysis.commonPeriodStart && latestAnalysis.commonPeriodEnd;

    return (
      <div>
        <div className='row'>
          <div className='col-lg-12' style={{marginBottom:'30px'}}>
            <div>
              <a href={'#myportfolios&myportfolioid=' + strategy.id} style={{textDecoration:'none',fontSize:'22px',color:'#05A9B1',fontWeight:'900'}}>{strategy.name}</a>
            </div>
            {hasCommonPeriod &&
              <div style={{fontSize:'14px'}}>
                {'As of ' + formatDate(latestAnalysis.commonPeriodEnd) + ' with a Common Period from ' + formatDate(latestAnalysis.commonPeriodStart) + ' to ' + formatDate(latestAnalysis.commonPeriodEnd)}
              </div>
            }
          </div>
        </div>
        <div className='row'>
          <div className='col-lg-4'>
            {this.renderKeyMetrics()}
          </div>
          <div className='col-lg-4'>
            {this.renderHoldingsChart()}
          </div>
          <div className='col-lg-4'>
            {this.renderPerformanceChart()}
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {};
}

export default connect(
  mapStateToProps,
  {},
)(Strategy);
